/**
 * Codex at-capacity alert — cron pass and operator surface (#2575).
 *
 * Settings field names are snake_case, matching the other internal
 * contracts. The evaluator that consumes them lives in platform-api;
 * this file is the wire shape only.
 */

import {
  CODEX_CAPACITY_AUDIENCE_DAYS_MAX,
  CODEX_CAPACITY_AUDIENCE_DAYS_MIN,
  CODEX_CAPACITY_CLEAR_PCT_MIN,
  CODEX_CAPACITY_FIRE_PCT_MAX,
  CODEX_CAPACITY_FIRE_PCT_MIN_EXCLUSIVE,
  CODEX_CAPACITY_QUIET_HOUR_MAX,
  CODEX_CAPACITY_QUIET_HOUR_MIN,
  CODEX_CAPACITY_RETENTION_HOURS_MIN,
  CODEX_CAPACITY_RETENTION_MINUTES_PER_HOUR,
  CODEX_CAPACITY_WINDOW_MINUTES_MIN,
} from './internal-codex-capacity.ts';

export type CodexCapacityAlertPhase = 'clear' | 'firing';
export type CodexCapacityAlertTransition = 'none' | 'fired' | 'recovered';
export type CodexCapacityAlertSkippedReason = 'concurrent_pass' | 'latch_race' | 'archive_stale';
export type CodexCapacityAlertDecisionAction = 'fire' | 'recover' | 'hold';
export type CodexCapacityAlertMailKind = 'elevated' | 'recovered';

/**
 * Who this pass would actually mail, on top of the evaluator verdict.
 *
 * Catch-up: `hold` while `firing` still sends elevated to accounts on
 * the open incident that have no history row. Quiet hours bind catch-up
 * the same way they bind a first fire. Preview, `/state`, and the send
 * path must all call this — a second copy is how "hold, nobody mailed"
 * and a real catch-up send diverge.
 */
export function sendKindForCodexCapacityPass(
  action: CodexCapacityAlertDecisionAction,
  phase: CodexCapacityAlertPhase,
  quietHoursActive: boolean,
): CodexCapacityAlertMailKind | null {
  if (action === 'fire') return 'elevated';
  if (action === 'recover') return 'recovered';
  if (action === 'hold' && phase === 'firing' && !quietHoursActive) return 'elevated';
  return null;
}

export type CodexCapacitySettingsIssueCode =
  | 'fire_pct'
  | 'clear_pct_min'
  | 'clear_pct_vs_fire'
  | 'window_minutes'
  | 'clear_window_minutes'
  | 'retention_hours'
  | 'rollup_lookback_minutes'
  | 'min_window_requests'
  | 'audience_days'
  | 'max_sends_per_pass'
  | 'cooldown_hours'
  | 'quiet_hours_pair'
  | 'quiet_hours_range'
  | 'quiet_hours_tz'
  | 'window_exceeds_retention'
  | 'clear_window_exceeds_retention';

export interface CodexCapacitySettingsIssue {
  readonly field: string;
  readonly code: CodexCapacitySettingsIssueCode;
  readonly message: string;
  readonly params: Record<string, string>;
}

/**
 * Same ranges as 0084 CHECKs. Platform PATCH and the ops-console form
 * both call this; a second copy is how a 400 and a blocked Save disagree.
 * Out of range is a refusal, never a silent clamp.
 */
export function validateCodexCapacityAlertSettings(
  merged: CodexCapacityAlertSettings,
): CodexCapacitySettingsIssue | null {
  if (
    !(
      merged.fire_pct > CODEX_CAPACITY_FIRE_PCT_MIN_EXCLUSIVE
      && merged.fire_pct <= CODEX_CAPACITY_FIRE_PCT_MAX
    )
  ) {
    return {
      field: 'fire_pct',
      code: 'fire_pct',
      message: `fire_pct must be > ${CODEX_CAPACITY_FIRE_PCT_MIN_EXCLUSIVE} and <= ${CODEX_CAPACITY_FIRE_PCT_MAX}`,
      params: {
        min: String(CODEX_CAPACITY_FIRE_PCT_MIN_EXCLUSIVE),
        max: String(CODEX_CAPACITY_FIRE_PCT_MAX),
      },
    };
  }
  if (merged.clear_pct < CODEX_CAPACITY_CLEAR_PCT_MIN) {
    return {
      field: 'clear_pct',
      code: 'clear_pct_min',
      message: `clear_pct must be >= ${CODEX_CAPACITY_CLEAR_PCT_MIN}`,
      params: { min: String(CODEX_CAPACITY_CLEAR_PCT_MIN) },
    };
  }
  if (merged.clear_pct > merged.fire_pct) {
    return {
      field: 'clear_pct',
      code: 'clear_pct_vs_fire',
      message: 'clear_pct must be <= fire_pct',
      params: {},
    };
  }
  if (merged.window_minutes < CODEX_CAPACITY_WINDOW_MINUTES_MIN) {
    return {
      field: 'window_minutes',
      code: 'window_minutes',
      message: `window_minutes must be >= ${CODEX_CAPACITY_WINDOW_MINUTES_MIN}`,
      params: { min: String(CODEX_CAPACITY_WINDOW_MINUTES_MIN) },
    };
  }
  if (merged.clear_window_minutes < CODEX_CAPACITY_WINDOW_MINUTES_MIN) {
    return {
      field: 'clear_window_minutes',
      code: 'clear_window_minutes',
      message: `clear_window_minutes must be >= ${CODEX_CAPACITY_WINDOW_MINUTES_MIN}`,
      params: { min: String(CODEX_CAPACITY_WINDOW_MINUTES_MIN) },
    };
  }
  if (merged.retention_hours < CODEX_CAPACITY_RETENTION_HOURS_MIN) {
    return {
      field: 'retention_hours',
      code: 'retention_hours',
      message: `retention_hours must be >= ${CODEX_CAPACITY_RETENTION_HOURS_MIN}`,
      params: { min: String(CODEX_CAPACITY_RETENTION_HOURS_MIN) },
    };
  }
  const retentionMinutes = merged.retention_hours * CODEX_CAPACITY_RETENTION_MINUTES_PER_HOUR;
  if (merged.window_minutes > retentionMinutes) {
    return {
      field: 'window_minutes',
      code: 'window_exceeds_retention',
      message: `window_minutes must be <= retention_hours * ${CODEX_CAPACITY_RETENTION_MINUTES_PER_HOUR}`,
      params: { max: String(retentionMinutes) },
    };
  }
  if (merged.clear_window_minutes > retentionMinutes) {
    return {
      field: 'clear_window_minutes',
      code: 'clear_window_exceeds_retention',
      message: `clear_window_minutes must be <= retention_hours * ${CODEX_CAPACITY_RETENTION_MINUTES_PER_HOUR}`,
      params: { max: String(retentionMinutes) },
    };
  }
  if (merged.rollup_lookback_minutes < CODEX_CAPACITY_WINDOW_MINUTES_MIN) {
    return {
      field: 'rollup_lookback_minutes',
      code: 'rollup_lookback_minutes',
      message: `rollup_lookback_minutes must be >= ${CODEX_CAPACITY_WINDOW_MINUTES_MIN}`,
      params: { min: String(CODEX_CAPACITY_WINDOW_MINUTES_MIN) },
    };
  }
  if (merged.min_window_requests < 0) {
    return {
      field: 'min_window_requests',
      code: 'min_window_requests',
      message: 'min_window_requests must be >= 0',
      params: {},
    };
  }
  if (
    merged.audience_days < CODEX_CAPACITY_AUDIENCE_DAYS_MIN
    || merged.audience_days > CODEX_CAPACITY_AUDIENCE_DAYS_MAX
  ) {
    return {
      field: 'audience_days',
      code: 'audience_days',
      message: `audience_days must be ${CODEX_CAPACITY_AUDIENCE_DAYS_MIN}..${CODEX_CAPACITY_AUDIENCE_DAYS_MAX}`,
      params: {
        min: String(CODEX_CAPACITY_AUDIENCE_DAYS_MIN),
        max: String(CODEX_CAPACITY_AUDIENCE_DAYS_MAX),
      },
    };
  }
  if (merged.max_sends_per_pass < 1) {
    return {
      field: 'max_sends_per_pass',
      code: 'max_sends_per_pass',
      message: 'max_sends_per_pass must be >= 1',
      params: {},
    };
  }
  if (merged.cooldown_hours < 0) {
    return {
      field: 'cooldown_hours',
      code: 'cooldown_hours',
      message: 'cooldown_hours must be >= 0',
      params: {},
    };
  }
  const start = merged.quiet_hours_start_hour;
  const end = merged.quiet_hours_end_hour;
  if ((start === null) !== (end === null)) {
    return {
      field: 'quiet_hours_start_hour',
      code: 'quiet_hours_pair',
      message: 'quiet_hours_start_hour and quiet_hours_end_hour must both be set or both be null',
      params: {},
    };
  }
  if (start !== null && end !== null) {
    if (
      start < CODEX_CAPACITY_QUIET_HOUR_MIN || start > CODEX_CAPACITY_QUIET_HOUR_MAX
      || end < CODEX_CAPACITY_QUIET_HOUR_MIN || end > CODEX_CAPACITY_QUIET_HOUR_MAX
    ) {
      return {
        field: 'quiet_hours_start_hour',
        code: 'quiet_hours_range',
        message: `quiet hours must be ${CODEX_CAPACITY_QUIET_HOUR_MIN}..${CODEX_CAPACITY_QUIET_HOUR_MAX}`,
        params: {
          min: String(CODEX_CAPACITY_QUIET_HOUR_MIN),
          max: String(CODEX_CAPACITY_QUIET_HOUR_MAX),
        },
      };
    }
  }
  if (merged.quiet_hours_tz.trim().length === 0) {
    return {
      field: 'quiet_hours_tz',
      code: 'quiet_hours_tz',
      message: 'quiet_hours_tz must be a non-empty string',
      params: {},
    };
  }
  return null;
}

export interface CodexCapacityAlertSettings {
  readonly enabled: boolean;
  readonly fire_pct: number;
  readonly clear_pct: number;
  readonly window_minutes: number;
  readonly clear_window_minutes: number;
  readonly min_window_requests: number;
  readonly audience_days: number;
  readonly max_sends_per_pass: number;
  readonly cooldown_hours: number;
  readonly quiet_hours_start_hour: number | null;
  readonly quiet_hours_end_hour: number | null;
  readonly quiet_hours_tz: string;
  readonly retention_hours: number;
  readonly rollup_lookback_minutes: number;
  readonly reason: string | null;
  readonly updated_at: string | null;
  readonly updated_by_ops_user_id: string | null;
}

export interface CodexCapacityAlertSettingsResponse {
  readonly settings: CodexCapacityAlertSettings;
}

export interface UpdateCodexCapacityAlertSettingsRequest {
  readonly enabled?: boolean;
  readonly fire_pct?: number;
  readonly clear_pct?: number;
  readonly window_minutes?: number;
  readonly clear_window_minutes?: number;
  readonly min_window_requests?: number;
  readonly audience_days?: number;
  readonly max_sends_per_pass?: number;
  readonly cooldown_hours?: number;
  readonly quiet_hours_start_hour?: number | null;
  readonly quiet_hours_end_hour?: number | null;
  readonly quiet_hours_tz?: string;
  readonly retention_hours?: number;
  readonly rollup_lookback_minutes?: number;
  readonly reason?: string | null;
}

/**
 * `POST /internal/ops/codex-capacity-alert` — one cron pass.
 *
 * No environment gate on this route: the gate lives in control-loops,
 * and this endpoint is also the manual re-run surface.
 */
export interface CodexCapacityAlertPassResponse {
  readonly ok: boolean;
  readonly alerts_enabled: boolean;
  readonly window_pct: number | null;
  readonly window_requests: number;
  readonly phase: CodexCapacityAlertPhase;
  readonly incident_id: string | null;
  readonly transition: CodexCapacityAlertTransition;
  readonly recipients: number;
  readonly suppressed: number;
  readonly deferred: number;
  readonly delivery_failures: number;
  readonly quiet_hours_skipped: boolean;
  readonly cooldown_skipped: boolean;
  readonly skipped_reason?: CodexCapacityAlertSkippedReason;
  readonly error?: string;
}

export interface CodexCapacityAlertStateResponse {
  readonly alerts_enabled: boolean;
  readonly phase: CodexCapacityAlertPhase;
  readonly incident_id: string | null;
  readonly clear_below_since: string | null;
  readonly last_evaluated_at: string | null;
  readonly last_window_pct: number | null;
  readonly last_window_requests: number | null;
  readonly last_notified_at: string | null;
  readonly window_pct: number | null;
  readonly window_requests: number;
  readonly decision: CodexCapacityAlertDecisionAction;
  readonly quiet_hours_active: boolean;
  readonly cooldown_active: boolean;
  /** Who this pass would mail, including catch-up. Null = no send. */
  readonly send_kind: CodexCapacityAlertMailKind | null;
  /** Accounts this pass would actually mail (owed, then capped). */
  readonly owed_count: number;
  readonly archive_bucket_start: string | null;
  readonly archive_updated_at: string | null;
  readonly archive_stale: boolean;
}

export interface CodexCapacityAlertPreviewRecipient {
  readonly account_id: string;
  readonly email_masked: string;
}

export interface CodexCapacityAlertPreviewResponse {
  readonly alerts_enabled: boolean;
  readonly window_pct: number | null;
  readonly window_requests: number;
  readonly decision: CodexCapacityAlertDecisionAction;
  readonly clear_below_since: string | null;
  readonly quiet_hours_active: boolean;
  readonly cooldown_active: boolean;
  readonly send_kind: CodexCapacityAlertMailKind | null;
  readonly owed_count: number;
  readonly archive_bucket_start: string | null;
  readonly archive_updated_at: string | null;
  readonly archive_stale: boolean;
  readonly recipients: readonly CodexCapacityAlertPreviewRecipient[];
  readonly suppressed: number;
}

export interface CodexCapacityAlertIncident {
  readonly id: string;
  readonly started_at: string;
  readonly fire_window_pct: number;
  readonly fire_window_requests: number;
  readonly peak_window_pct: number;
  readonly recovered_at: string | null;
  readonly recovery_window_pct: number | null;
  readonly fire_recipients: number;
  readonly recovery_recipients: number;
}

export interface CodexCapacityAlertIncidentsResponse {
  readonly incidents: readonly CodexCapacityAlertIncident[];
}

export interface CodexCapacityAlertHistoryEntry {
  readonly id: string;
  readonly incident_id: string;
  readonly account_id: string;
  readonly kind: CodexCapacityAlertMailKind;
  readonly sent_at: string;
  readonly to_address: string;
  readonly window_pct: number;
  readonly test_send: boolean;
}

export interface CodexCapacityAlertHistoryResponse {
  readonly entries: readonly CodexCapacityAlertHistoryEntry[];
}

export interface CodexCapacityAlertSuppression {
  readonly account_id: string;
  readonly account_email: string | null;
  readonly reason: string | null;
  readonly created_at: string;
  readonly created_by_ops_user_id: string;
}

export interface CodexCapacityAlertSuppressionsResponse {
  readonly suppressions: readonly CodexCapacityAlertSuppression[];
}

export interface CreateCodexCapacityAlertSuppressionRequest {
  readonly account_id: string;
  readonly reason?: string | null;
}

export interface CreateCodexCapacityAlertSuppressionResponse {
  readonly suppression: CodexCapacityAlertSuppression;
}

export interface DeleteCodexCapacityAlertSuppressionResponse {
  readonly removed: boolean;
}

export interface CodexCapacityExcludedSku {
  readonly sku_name: string;
  readonly created_at: string;
  readonly created_by_ops_user_id: string | null;
}

export interface CodexCapacityExcludedSkusResponse {
  readonly excluded_skus: readonly CodexCapacityExcludedSku[];
}

export interface CreateCodexCapacityExcludedSkuRequest {
  readonly sku_name: string;
}

export interface CreateCodexCapacityExcludedSkuResponse {
  readonly excluded_sku: CodexCapacityExcludedSku;
}

export interface DeleteCodexCapacityExcludedSkuRequest {
  readonly sku_name: string;
}

export interface DeleteCodexCapacityExcludedSkuResponse {
  readonly removed: boolean;
}

export interface CodexCapacityAlertTestSendRequest {
  readonly account_ids: readonly string[];
  readonly kind: CodexCapacityAlertMailKind;
}

export interface CodexCapacityAlertTestSendResponse {
  readonly ok: boolean;
  readonly sent: number;
  readonly delivery_failures: number;
  readonly error?: string;
}
